import type { ObraSummary } from '@obras/shared';
import { formatPercent } from '@obras/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { Bell, HardHat } from 'lucide-react';
import { AnimatePresence, motion, type Variants } from 'motion/react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Badge, ObraStatusBadge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { CoverImage } from '../../components/ui/Cover.js';
import { Avatar, Skeleton } from '../../components/ui/Feedback.js';

/** Até onde a fila de entrada das linhas vai: da nona em diante, todas juntas. */
const LINHAS_ESCALONADAS = 8;
const PASSO = 0.03;

/** O corpo da tabela entra e sai como um bloco, na troca de página, filtro ou ordem. */
const CORPO: Variants = {
  hidden: {},
  shown: {},
  gone: { opacity: 0, transition: { duration: 0.14, ease: EASE_OUT_QUINT } },
};

/** Cada linha sobe 8px e assenta no seu passo da fila. */
const LINHA: Variants = {
  hidden: { opacity: 0, y: 8 },
  shown: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.32,
      ease: EASE_OUT_QUINT,
      delay: Math.min(index, LINHAS_ESCALONADAS) * PASSO,
    },
  }),
};

const CABECALHO = [
  { rotulo: 'Obra', alinhamento: 'text-left' },
  { rotulo: 'Fiscal', alinhamento: 'text-left' },
  { rotulo: 'Situação', alinhamento: 'text-left' },
  { rotulo: 'Execução', alinhamento: 'text-right' },
  { rotulo: 'Prazo decorrido', alinhamento: 'text-right' },
  { rotulo: 'Notificações', alinhamento: 'text-right' },
] as const;

function Cabecalho() {
  return (
    <thead className="bg-sunken">
      <tr className="text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase *:border-b-2 *:border-line-2">
        {CABECALHO.map((coluna) => (
          <th key={coluna.rotulo} scope="col" className={`px-5 py-3 ${coluna.alinhamento}`}>
            {coluna.rotulo}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * A listagem em linhas: as mesmas obras da página dos cartões, numa tabela
 * (design.md §8, Lista de obras).
 *
 * Para quem compara obras entre si — execução contra prazo, quem fiscaliza o
 * quê — e não precisa da foto grande. A linha inteira é clicável e escurece
 * sob o ponteiro, porque aqui há ação; o nome é o link de verdade, para o
 * teclado e para abrir em outra aba.
 */
export function ObrasLista({ obras }: { obras: readonly ObraSummary[] }) {
  const navigate = useNavigate();

  return (
    <Card square className="overflow-x-auto">
      <table className="w-full min-w-[920px]">
        <caption className="sr-only">Obras do recorte</caption>
        <Cabecalho />

        <AnimatePresence mode="wait">
          <motion.tbody
            key={obras.map((obra) => obra.id).join(',')}
            variants={CORPO}
            initial="hidden"
            animate="shown"
            exit="gone"
          >
            {obras.map((obra, index) => (
              <motion.tr
                key={obra.id}
                variants={LINHA}
                custom={index}
                onClick={(event) => {
                  // O link do nome já leva à obra; a linha cobre o resto.
                  if ((event.target as HTMLElement).closest('a')) return;
                  void navigate({ to: '/obras/$obraId', params: { obraId: obra.id } });
                }}
                className="cursor-pointer border-t border-line transition-colors duration-100 hover:bg-sunken"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="relative size-10 shrink-0 overflow-hidden bg-sunken">
                      {obra.coverUrl ? (
                        <CoverImage src={obra.coverUrl} />
                      ) : (
                        <span className="grid size-full place-items-center text-ink-3">
                          <HardHat size={16} strokeWidth={1.5} aria-hidden />
                        </span>
                      )}
                    </span>
                    <div className="min-w-0">
                      <Link
                        to="/obras/$obraId"
                        params={{ obraId: obra.id }}
                        title={obra.name}
                        className="block max-w-[340px] truncate text-[14px] font-medium tracking-tight"
                      >
                        {obra.name}
                      </Link>
                      <span className="block font-mono text-[12px] text-ink-3">
                        {obra.contractNumber ?? 'sem contrato'}
                      </span>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-3 text-[13px]">
                  {obra.responsibleFiscal ? (
                    <span className="flex max-w-[200px] items-center gap-2">
                      <Avatar
                        name={obra.responsibleFiscal.name}
                        url={obra.responsibleFiscal.avatarUrl}
                        size={20}
                      />
                      <span className="truncate">{obra.responsibleFiscal.name}</span>
                    </span>
                  ) : (
                    <span className="text-ink-3">sem fiscal</span>
                  )}
                </td>

                <td className="px-5 py-3">
                  <ObraStatusBadge status={obra.status} />
                </td>

                <td className="numeral px-5 py-3 text-right text-[14px]">
                  {formatPercent(obra.executionProgress)}
                </td>

                <td className="numeral px-5 py-3 text-right text-[14px] text-ink-2">
                  {formatPercent(obra.timeProgress)}
                </td>

                <td className="px-5 py-3 text-right">
                  {obra.updatesSinceLastView > 0 ? (
                    <Badge tone="primary" icon={Bell} solid>
                      {obra.updatesSinceLastView}
                    </Badge>
                  ) : (
                    <span className="text-ink-3">—</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </motion.tbody>
        </AnimatePresence>
      </table>
    </Card>
  );
}

/** A tabela enquanto a página não chega: as mesmas colunas, as mesmas alturas. */
export function ObrasListaSkeleton() {
  return (
    <Card square className="overflow-x-auto" aria-hidden>
      <table className="w-full min-w-[920px]">
        <Cabecalho />
        <tbody>
          {Array.from({ length: 8 }, (_, linha) => (
            <tr key={linha} className="border-t border-line">
              <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="size-10 shrink-0 rounded-none" />
                  <div className="flex-1">
                    <Skeleton className="h-3.5 w-48 rounded-none" />
                    <Skeleton className="mt-1.5 h-3 w-20 rounded-none" />
                  </div>
                </div>
              </td>
              <td className="px-5 py-3">
                <Skeleton className="h-3.5 w-28 rounded-none" />
              </td>
              <td className="px-5 py-3">
                <Skeleton className="h-5 w-24 rounded-none" />
              </td>
              {[0, 1, 2].map((coluna) => (
                <td key={coluna} className="px-5 py-3">
                  <Skeleton className="ml-auto h-3.5 w-10 rounded-none" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
